import ListFormItem from "@arelia-components/form/ListFormItem";
import { Account } from "@arelia-types/index";
import { Button, Card, CardContent, List, TextField, useMediaQuery, Typography, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormHelperText } from "@mui/material";
import { Box } from "@mui/system";
import { FormikProps } from "formik";
import { ChangeEvent, useState, useRef } from "react";
import theme from "src/theme";
import { EditForm } from "./CaptureFormContainer";
import { formConfig } from "./constants";
import UnauthorizedHeader from "./UnauthorizedHeader";
import manPlayingAGuitarAnimation from "@arelia-assets/lottie-animations/man-playing-a-guitar.json"
import Lottie from "lottie-react";
import { countryNames } from "src/widgets/countries/constants";
import { useQuery } from "react-query";
import { http } from "src/api/http";
import ReCAPTCHA from "react-google-recaptcha";
import { useParams } from "react-router-dom";

type Props = {
  formik: FormikProps<EditForm>;
  handeChange: (name: keyof EditForm) => (event: ChangeEvent<HTMLInputElement>) => void;
  account: Account | undefined;
  submitted: boolean;
};

export default function CaptureFormPresenter({ formik, handeChange, account, submitted }: Props) {
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const [isCaptureFormEnabled, setIsCaptureFormEnabled] = useState(true);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { publicId } = useParams();

  useQuery<boolean>({
    queryKey: [`/api/my-fans/${publicId}/capture-form-status`],
    queryFn: () => http(`/api/my-fans/${publicId}/capture-form-status`),
    onSuccess: (data) => {
      setIsCaptureFormEnabled(data);
    }
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('smsConsent', event.target.checked);
    if (event.target.checked && formik.errors.smsConsent) {
      formik.setFieldError('smsConsent', undefined);
    }
  };

  const showForm = isCaptureFormEnabled && !submitted;

  const handleCaptchaChange = (value: string | null) => {
    formik.setFieldValue('captchaToken', value);
    if (value && formik.errors.captchaToken) {
      formik.setFieldError('captchaToken', undefined);
    }
  };

  const countryRenderer = (field: keyof EditForm) => {
    const value = formik.values[field];

    return <FormControl fullWidth>
      <InputLabel id="country-select">Country</InputLabel>
      <Select
        labelId="country-select"
        value={value}
        label="Country"
        onChange={event => {
          handeChange(field)(event as ChangeEvent<HTMLInputElement>);
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 170,
            },
          },
        }}
      >
        {countryNames.map((country, index) => <MenuItem key={index} value={country}>{country}</MenuItem>)}
      </Select>
    </FormControl>
  }

  const fieldRenderer = (field: keyof EditForm) => {
    const value = formik.values[field];
    const hasError = (field === 'fullName' || field === 'phone') &&
      formik.touched[field] &&
      Boolean(formik.errors[field]);
    const errorMessage = (field === 'fullName' || field === 'phone') &&
      formik.touched[field] ?
      formik.errors[field] as string :
      "";

    if (field === "country") {
      return countryRenderer(field)
    }

    return <TextField
      value={value || ""}
      onChange={handeChange(field)}
      size="small"
      type="text"
      fullWidth
      error={hasError}
      helperText={errorMessage}
    />;
  }

  return (
    <Box
      bgcolor={theme.palette.background.default}
      flexGrow={1}
      height={1}
      display="flex"
      flexDirection="column"
      gap={isMd ? "16px" : "34px"}
      sx={{
        overflowY: "auto",
      }}
    >
      <UnauthorizedHeader
        title={account?.band?.name ?? "Band"}
        banner={account?.captureFormBackgroundUrl ?? account?.backgroundUrl}
      />
      {showForm && <Box p={isMd ? 2 : 5} pt={2} sx={styles.container}>
        <Typography variant="h2">Subscribe to receive News & Releases!</Typography>
        <Card sx={styles.card(isMd)}>
          <CardContent sx={{
            marginTop: "24px",
            justifyContent: "flex-start"
          }}>
            <List>
              {formConfig.map(({ label, field }, index) => (
                <ListFormItem key={index} label={label}>
                  {fieldRenderer(field)}
                </ListFormItem>
              ))}
              <ListFormItem key="100" label="">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.smsConsent || false}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={`I agree to receive informational messaging from ${account?.band?.name || "Band"} to the phone number provided above. Data rates may apply, reply STOP to opt out.`}
                />
                {formik.touched.smsConsent && formik.errors.smsConsent && (
                  <FormHelperText error>{formik.errors.smsConsent as string}</FormHelperText>
                )}
              </ListFormItem>
              <ListFormItem key="101" label="">
                <Box sx={{ mt: 2 }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY as string || ""}
                    onChange={handleCaptchaChange}
                  />
                  {formik.touched.captchaToken && formik.errors.captchaToken && (
                    <FormHelperText error>{formik.errors.captchaToken as string}</FormHelperText>
                  )}
                </Box>
              </ListFormItem>
            </List>
            <Button
              variant="contained"
              onClick={() => {
                Object.keys(formik.values).forEach(field => {
                  formik.setFieldTouched(field as keyof EditForm, true);
                });

                formik.validateForm().then(errors => {
                  if (Object.keys(errors).length === 0) {
                    formik.submitForm();
                  }
                });
              }}
            >
              Subscribe
            </Button>
          </CardContent>
        </Card>
      </Box>}

      {!showForm && <Box sx={styles.lottieContainer}>
        <Lottie
          animationData={manPlayingAGuitarAnimation}
          style={styles.lottie}
        />
        {submitted ? (
          <Typography variant="h3">Thank you for reaching out to us!</Typography>
        ) : (
          <Typography variant="h3">This subscription form is currently not available.</Typography>
        )}
      </Box>}
    </Box>
  );
}

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  lottieContainer: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    height: "100%"
  },
  card(isMd: boolean) {
    return {
      width: isMd ? "100%" : "50%",
    }
  },
  cardContent: {
    justifyContent: "flex-start"
  },
  lottie: {
    height: 300
  }
}
